import { useMemo } from "react"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import CustomerBillingInvoiceTable from "@/v2-backoffice-client/customer/billing/CustomerBillingInvoiceTable"
import CustomerLicense from "@/v2-backoffice-client/customer/license/CustomerLicense"
import { useLicenseQuery } from "@/v2-backoffice-client/license/license.query"
import { useLicenseAccountsQuery } from "@/v2-backoffice-client/license/account/license.account.query"
import { useLicenseAccountInvoicesQuery } from "@/v2-backoffice-client/license/account/view/license.account.invoices.query"
import { useLicenseAccountMatchesQuery } from "@/v2-backoffice-client/license/account/view/license.account.matches.query"
import LicenseAccountMatchPanel from "@/v2-backoffice-client/license/account/view/LicenseAccountMatchPanel"
import LicenseAccountViewInfo from "@/v2-backoffice-client/license/account/view/LicenseAccountViewInfo"
import LicenseAccountViewToolbar from "@/v2-backoffice-client/license/account/view/LicenseAccountViewToolbar"

// The billing-account panel (?v=billingAccount:<id>). Fed by the same
// getBillingAccounts list the board renders, so board→panel opens from the
// Apollo cache. Invoices are the account's own objects (keyed on the account
// row, so unmatched accounts show theirs too — often the fastest way to
// recognise who an orphan belongs to); the Customers tab lists every
// customer carrying the account's org number, with the link/acknowledge
// decisions an operator records about them; licenses hang off the matched
// customer, so that tab explains itself when there is none.
function LicenseAccountViewSheet() {
  const { id } = useAppViewSheetOpener()
  const accountId = Number(id)

  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
  } = useLicenseAccountsQuery({ skip: !accountId })
  const accounts = useMemo(
    () => accountsData?.getBillingAccounts ?? [],
    [accountsData?.getBillingAccounts],
  )
  const account = accounts.find((candidate) => candidate.Id === accountId)

  const {
    data: invoicesData,
    loading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useLicenseAccountInvoicesQuery({
    skip: !accountId,
    variables: { accountId },
  })
  const invoices = invoicesData?.getBillingAccountInvoices ?? []

  // Same query (and variables) CustomerLicense runs for the tab's content —
  // one cache entry feeds both the badge and the table.
  const customerId = account?.CustomerId
  const { data: licenseData, loading: licenseLoading } = useLicenseQuery({
    skip: !customerId,
    variables: { customerId: customerId ?? 0 },
  })
  const licenseCount = customerId ? licenseData?.getLicenses.length : undefined

  // Same query (and variables) the match panel runs — one cache entry feeds
  // both the badge and the candidate list.
  const { data: matchesData, loading: matchesLoading } =
    useLicenseAccountMatchesQuery({
      skip: !accountId,
      variables: { accountId },
    })
  const matchCount = matchesData?.getBillingAccountMatches.length

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={accountsLoading}>
          {account?.CompanyName}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <LicenseAccountViewInfo account={account} isLoading={accountsLoading} />
        <LicenseAccountViewToolbar account={account} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="license-account-view-tabs"
        defaultValue="invoices"
        isLoading={accountsLoading}
        error={accountsError}
        isEmpty={!account}
        emptyLabel="Billing account not found."
      >
        <Tabs.List>
          <Tabs.Trigger
            value="invoices"
            isLoading={invoicesLoading}
            count={invoices.length}
          >
            Invoices
          </Tabs.Trigger>
          <Tabs.Trigger
            value="customers"
            isLoading={matchesLoading}
            count={matchCount}
          >
            Customers
          </Tabs.Trigger>
          <Tabs.Trigger
            value="licenses"
            isLoading={licenseLoading}
            count={licenseCount}
          >
            Licenses
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="invoices" className="min-h-0">
          <CustomerBillingInvoiceTable
            invoices={invoices}
            isLoading={invoicesLoading}
            error={invoicesError}
            onRefresh={() => {
              void refetchInvoices()
            }}
          />
        </Tabs.Content>

        <Tabs.Content value="customers" className="min-h-0">
          {account && <LicenseAccountMatchPanel account={account} />}
        </Tabs.Content>

        <Tabs.Content value="licenses" className="min-h-0">
          {account &&
            (customerId ? (
              <CustomerLicense
                customerId={customerId}
                customerName={account.CustomerName ?? undefined}
              />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">
                No licenses — licenses attach to customers, and this account
                isn&apos;t linked to one yet
                {account.UnmatchedReason
                  ? ` (${account.UnmatchedReason.toLowerCase()})`
                  : ""}
                . Fix the link on the Billing Accounts board and the hourly
                mirror heals the rest.
              </p>
            ))}
        </Tabs.Content>
      </Tabs>
    </>
  )
}

export default LicenseAccountViewSheet
